import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink
} from '@chain-zen/ui'
import { encode } from 'base-64'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { SAVE_CLIENT_DETAILS, SAVE_STATE_PARAM } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import { envConfig } from '../../../lib/env-config'
import { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type SaveClientDetailsInput = {
  appType: AppTypeEnum
}

type SaveStateParamInput = {
  appType: AppTypeEnum
  id: string
}

/**
 * This is the modal for the Salesforce OAuth app.
 */
export function SalesforceOAuthModal({ isOpen, onClose, appType }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [clientID, setClientID] = useState('')
  const [oauthUrl, setOauthUrl] = useState<string | null>(null)

  const isProduction = appType === AppTypeEnum.RevenovaProduction
  const appText = isProduction ? 'Revenova Production' : 'Revenova Sandbox'

  useEffect(() => {
    if (oauthUrl) {
      window.location.href = oauthUrl
    }
  }, [oauthUrl])

  const [saveClientDetails, { loading: saveClientLoading }] = useMutation<
    { saveClientDetails: { id: string } },
    SaveClientDetailsInput
  >(SAVE_CLIENT_DETAILS, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: (data) => {
      setClientID(data.saveClientDetails.id)

      const stateParam = uuidv4()
      saveStateParam({ variables: { appType: appType as AppTypeEnum, id: stateParam } })
    },
    onError: (err) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const [saveStateParam, { loading: saveParamLoading }] = useMutation<
    { saveStateParam: { id: string } },
    SaveStateParamInput
  >(SAVE_STATE_PARAM, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: (data) => {
      const state = {
        href: window.location.href,
        origin: window.location.origin,
        path: window.location.pathname,
        stateParam: data.saveStateParam.id
      }
      const stateJson = JSON.stringify(state)
      const base64State = encode(stateJson)

      const instance = isProduction ? 'login' : 'test'
      const thirdPartyOAuthURL = `https://${instance}.salesforce.com/services/oauth2/authorize?client_id=${clientID}&response_type=code&redirect_uri=${envConfig.oauthCallbackUrl}/salesforce&scope=full+refresh_token&state=${base64State}`

      setOauthUrl(thirdPartyOAuthURL)
    },
    onError: (err) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    }
  })

  const handleClose = () => {
    setFormError('')
    onClose()
  }

  const handleEnable = () => {
    saveClientDetails({
      variables: { appType: appType as AppTypeEnum }
    })
  }

  const loading = saveClientLoading || saveParamLoading

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{`Enable ${appText} for Chain`}</DialogTitle>
          <DialogDescription>
            Enabling this integration requires a Salesforce account with Revenova installed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <p className="text-sm">
            When enabling, you will be prompted to give Chain access to install the Chain-Salesforce app by
            authenticating with your Salesforce login, and clicking "Allow"
          </p>

          {isProduction ? (
            <Alert
              variant="warning"
              description="You must also enable the Sandbox Revenova app to successfully setup a Revenova integration"
            />
          ) : (
            <Alert
              variant="warning"
              description="You must also enable the Production Revenova app to successfully setup a Revenova integration"
            />
          )}

          {!!formError && <Alert variant="error" description={formError} />}

          <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
            <a
              href="https://help.trychain.com/en/collections/salesforce"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              Read the integration guide
              <ExternalLink className="h-4 w-4" />
            </a>

            <div className="space-x-2">
              <Button variant="outline" onClick={handleClose} type="button">
                Cancel
              </Button>
              <Button type="button" onClick={handleEnable} disabled={loading}>
                {loading ? 'Enabling...' : 'Enable'}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
